<template>
  <div class="w-full pt-10 -mb-20 max-w-6xl mx-auto px-4 relative">
    <!-- Gradiente Esquerdo (Fade transparente para preto) -->
    <div
        class="pointer-events-none absolute z-30 top-0 left-0 h-full w-24 lg:w-40"
        :style="{ background: `linear-gradient(to right, ${pageColor} 75%, transparent 100%)` }"
    ></div>
    <div
        class="pointer-events-none absolute z-30 top-0 right-0 h-full w-24 lg:w-40"
        :style="{ background: `linear-gradient(to left, ${pageColor} 75%, transparent 100%)` }"
    ></div>

    <!-- Setas -->
    <button
        class="absolute left-2 top-1/2 z-40 -translate-y-1/2 bg-black/70 text-white p-3 rounded-full hover:scale-110 transition"
        @click="swiperRef?.slidePrev()"
    >
      <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
      </svg>
    </button>
    <button
        class="absolute right-2 top-1/2 z-40 -translate-y-1/2 bg-black/70 text-white p-3 rounded-full hover:scale-110 transition"
        @click="swiperRef?.slideNext()"
    >
      <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
      </svg>
    </button>

    <!-- Swiper -->
    <Swiper
        :modules="[EffectCoverflow]"
        effect="coverflow"
        grab-cursor
        centered-slides
        loop
        slides-per-view="auto"
        :coverflow-effect="{
        rotate: 0,
        stretch: 0,
        depth: 250,
        modifier: 1.5,
        slideShadows: false
      }"
        @swiper="onSwiperInit"
        @slideChange="updateActiveIndex"
        class="mySwiper"
    >
      <SwiperSlide
          v-for="(item, index) in normalizedItems"
          :key="index"
          class="relative flex items-center justify-center rounded-2xl overflow-hidden transition-all duration-500"
          :class="[
          getBlurClass(index),
          getOpacityClass(index),
          shouldHide(index) ? 'lg:hidden' : ''
        ]"
      >
        <component
            :is="item.type === 'youtube' ? 'iframe' : 'img'"
            v-bind="getMediaProps(item)"
            class="object-cover rounded-2xl w-full h-full"
        />
      </SwiperSlide>
    </Swiper>
  </div>
</template>



<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { EffectCoverflow } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/effect-coverflow'

const props = defineProps({
  images: { type: Array, required: true },
  pageColor: { type: String, default: '#111' }, // Adicione essa prop
})

const swiperInstance = ref(null)
const activeIndex    = ref(0)

function onSwiperInit(swiper) {
  swiperInstance.value = swiper
  activeIndex.value = swiper.realIndex
}
function updateActiveIndex() {
  if (swiperInstance.value) activeIndex.value = swiperInstance.value.realIndex
}

const swiperRef = {
  slideNext: () => swiperInstance.value?.slideNext(),
  slidePrev: () => swiperInstance.value?.slidePrev(),
}

const isDesktop = ref(typeof window !== 'undefined' && window.innerWidth >= 1024)
function handleResize() { isDesktop.value = window.innerWidth >= 1024 }
onMounted(() => window.addEventListener('resize', handleResize))
onUnmounted(() => window.removeEventListener('resize', handleResize))

const normalizedItems = computed(() =>
    (Array.isArray(props.images) ? props.images : [])
        .filter(Boolean)
        .map((item) => (typeof item === 'string' ? { type: 'image', src: item } : item))
)

function getBlurClass(index) {
  const total = normalizedItems.value.length
  let delta = index - activeIndex.value
  if (delta > total / 2) delta -= total
  if (delta < -total / 2) delta += total
  return ['blur-none', 'blur-[1px]', 'blur-[10px]'][Math.min(Math.abs(delta), 2)]
}
function getOpacityClass(index) {
  const total = normalizedItems.value.length
  let delta = index - activeIndex.value
  if (delta > total / 2) delta -= total
  if (delta < -total / 2) delta += total
  return ['opacity-100', 'opacity-70', 'opacity-30'][Math.min(Math.abs(delta), 2)]
}
function shouldHide(index) {
  if (!isDesktop.value) return false
  const total = normalizedItems.value.length
  let delta = index - activeIndex.value
  if (delta > total / 2) delta -= total
  if (delta < -total / 2) delta += total
  return Math.abs(delta) > 2
}

function getMediaProps(item) {
  if (!item) return {}
  if (item.type === 'youtube') {
    const id = item.src.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/)
    return {src: id ? `https://www.youtube.com/embed/${id[1]}` : '', frameborder: '0', allowfullscreen: true}
  }
  return {src: item.src, alt: 'Imagem do carrossel'}
}
</script>

<style scoped>
.mySwiper {
  /* RESPONSIVO */
  width: 100%;
  max-width: 100vw;
  height: 350px;
}

@media (min-width: 640px) {
  .mySwiper {
    height: 400px;
  }
}

@media (min-width: 1024px) {
  .mySwiper {
    height: 90vh;
    max-width: 1200px;
  }
}

.swiper-slide {
  /* RESPONSIVO */
  width: 95vw;
  max-width: 340px;
  height: 100%;
}

@media (min-width: 640px) {
  .swiper-slide {
    max-width: 400px;
  }
}

@media (min-width: 1024px) {
  .swiper-slide {
    width: 300px;
    height: 600px;
  }
}
</style>
